import { Components } from "@mui/material/styles/components";

import { isSmallDesktop } from "@/hooks/layout";

const components: Components = {
  MuiToolbar: {
    defaultProps: {
      disableGutters: true,
    },
  },
  MuiButtonBase: {
    defaultProps: {
      disableRipple: true,
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        color: "#000",
      },
    },
  },
  MuiFormLabel: {
    styleOverrides: {
      root: {
        color: "rgba(251, 244, 236, 0.6)",
        fontFamily: "Raleway, sans-serif",
      },
    },
  },
  MuiFormControl: {
    styleOverrides: {
      root: {
        color: "#FBF4EC",
        ".MuiFormControlLabel-root": {
          alignItems: "start",
          [isSmallDesktop]: {
            alignItems: "center",
          },
          marginLeft: "0",
        },
        ".MuiFormControlLabel-label": {
          fontSize: "0.9375rem",
          fontFamily: "Raleway, sans-serif",
          fontWeight: 400,
          lineHeight: "150%",
          paddingLeft: "8px",
          color: "#e0d2c1",
          textAlign: "start",
          [isSmallDesktop]: {
            textAlign: "center",
          },

          "&.Mui-disabled": {
            color: "rgba(251, 244, 236, 0.3) !important",
          },
        },
        ".MuiFormLabel-root": {
          color: "rgba(251, 244, 236, 0.6)",
          fontSize: "0.9375rem",
          fontFamily: "Raleway, sans-serif",
          fontWeight: 400,
          lineHeight: "150%",
          paddingLeft: "8px",
          "&.Mui-focused": {
            color: "#fbf4ec",
          },
        },
      },
    },
  },
  MuiCheckbox: {
    styleOverrides: {
      root: {
        padding: 0,
        color: "#FFDCA8",
        "&.Mui-checked": {
          color: "#FFDCA8",

          "&.Mui-disabled": {
            color: "#AC8D75 !important",
          },
        },
        "& .MuiSvgIcon-root": { scale: "1.3" },
      },
    },
  },
  MuiDialog: {
    defaultProps: {
      disableScrollLock: true,
    },
    styleOverrides: {
      paper: {
        boxShadow: "none",
        margin: 0,
        width: "100%",
        [isSmallDesktop]: {
          overflow: "hidden",
          width: "70%",
          maxWidth: "640px",
        },

        borderRadius: "10px",

        display: "inline-block",
        position: "relative",
        zIndex: 0,

        border: "double 2px transparent",
        backgroundOrigin: "border-box",
        backgroundClip: "content-box, border-box",
        backgroundImage:
          "linear-gradient(#3d2e24, #3d2e24), linear-gradient(93deg, #BA905C 0.82%, #FFD29B 33.73%, #FFE0B1 47.65%, #FFD29B 61.53%, #BA905C 99.48%)",
      },
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: {
        background: "transparent",
        overflowY: "hidden",
        padding: 0,
      },
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        background: "transparent",
        overflowY: "hidden",
        paddingBottom: 0,
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        ".MuiOutlinedInput-root": {
          fontFamily: "Raleway, sans-serif !important",
          fontWeight: 400,
          fontSize: "0.9375rem",
          lineHeight: "120%",
          letterSpacing: "0.02em",
          color: "#ffffff",
          borderRadius: "8px",
          paddingRight: "8px",
          border: "1px solid rgba(235, 194, 142, 0.25)",

          backgroundColor: "rgba(235, 194, 142, 0.06)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",

          transition: "all 0.2s ease",

          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            border: "0",
          },

          "&.Mui-focused": {
            border: "1px solid rgba(235, 194, 142, 0.5)",
            backgroundColor: "rgba(235, 194, 142, 0.1)",
            boxShadow: "0 0 12px rgba(186, 144, 92, 0.15)",
          },

          "&:hover:not(.Mui-focused)": {
            borderColor: "rgba(235, 194, 142, 0.35)",
          },

          "&.Mui-error": {
            color: "#e47a6a",
            border: "1px solid #e47a6a",
          },

          "&.Mui-disabled": {
            backgroundColor: "rgba(81, 58, 40, 0.4)",
            opacity: 0.5,
          },
        },
        ".MuiOutlinedInput-input": {
          padding: "12px 16px",
          "&:-webkit-autofill, -webkit-autofill:hover, -webkit-autofill:focus, -webkit-autofill:active": {
            caretColor: "#ffffff",
            WebkitBoxShadow: "inset 0 100vw rgba(235, 194, 142, 0.06)",
            WebkitTextFillColor: "#ffffff",
            marginRight: "-8px",
          },
          "&::placeholder": {
            color: "rgba(255, 255, 255, 0.5)",
            opacity: 1,
          },

          "&.Mui-disabled": {
            "-webkit-text-fill-color": "initial !important",
          },

          textarea: {
            height: "100%",
          },
        },

        fieldset: {
          border: "none",
        },
      },
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: {
        position: "absolute",
        bottom: "-22px",

        fontFamily: "Raleway",

        fontWeight: 500,

        fontSize: "0.75rem",

        marginLeft: 0,

        "&.Mui-error": {
          color: "#E0D2C1",
        },
      },
    },
  },
  MuiAccordion: {
    styleOverrides: {
      root: {
        "&.MuiPaper-root": {
          boxShadow: "none",
          borderRadius: "12px",
          // A neutral white hairline at rest, not a gold one. Gold means
          // "active" on this site, and every closed accordion wearing a faint
          // gold outline made a page of them read as a page of live controls.
          // Gold is kept for the OPEN one below, where it is earned.
          border: "1px solid rgba(255, 255, 255, 0.14)",
          background: "rgba(81, 58, 40, 0.6)",
          // Border only. The background is deliberately NOT in this transition:
          // hover must not change the fill, just brighten the outline.
          transition: "border-color 0.3s ease",

          // Gold on hover — white at rest, warm on approach, fully gold when
          // open, so the outline reads as one gradual "this is the live one".
          "&:hover": {
            borderColor: "rgba(235, 194, 142, 0.3)",
          },

          "&.Mui-expanded": {
            margin: 0,
            borderColor: "rgba(235, 194, 142, 0.4)",
            boxShadow: "0 0 20px rgba(186, 144, 92, 0.3)",
          },

          "&:before": {
            display: "none",
          },
        },
      },
    },
  },
  MuiAccordionSummary: {
    styleOverrides: {
      root: {
        paddingInline: "20px",
        minHeight: "56px",
        "&.Mui-expanded": {
          minHeight: "56px",
        },
        "& .MuiAccordionSummary-expandIconWrapper": {
          color: "rgba(235, 194, 142, 0.5)",
          transition: "color 0.3s ease, transform 0.3s ease",
        },
        "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
          color: "#ebc28e",
        },
      },
    },
  },
  MuiAccordionDetails: {
    styleOverrides: {
      root: {
        paddingInline: "20px",
        paddingBottom: "20px",
        paddingTop: "0",
      },
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: {
        fontFamily: "Raleway",
        fontWeight: 400,
        fontSize: "1rem",
        lineHeight: "1rem",
        letterSpacing: "0.01rem",
        color: "#FBF4EC",

        minWidth: "69px",

        minHeight: "48px",
        padding: 0,

        display: "inline-block",
        position: "relative",
        zIndex: 0,

        border: 0,

        transition: "background-color .3s ease-out",

        "&::before": {
          content: "''",
          position: "absolute",
          zIndex: 0,
          inset: 0,
          padding: "2px",
          borderRadius: "8px",
          background:
            "linear-gradient(93deg, #BA905C 0.82%, #FFD29B 33.73%, #FFE0B1 47.65%, #FFD29B 61.53%, #BA905C 99.48%)",
          mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
          "-webkit-mask-composite": "xor",
          "mask-composite": "exclude",
        },

        "&.Mui-selected": {
          color: "#191A1A",
          background:
            "linear-gradient(93deg, #BA905C 0.82%, #FFD29B 33.73%, #FFE0B1 47.65%, #FFD29B 61.53%, #BA905C 99.48%)",

          div: {
            backgroundColor: "transparent",
          },
        },

        "&:hover": {
          backgroundColor: "rgba(57, 43, 30, 0.8)",
        },
      },
    },
  },
  MuiToggleButtonGroup: {
    styleOverrides: {
      root: {
        "& .MuiToggleButtonGroup-grouped": {
          margin: "4px",
          "&:not(:first-of-type)": {
            borderRadius: "8px",
            marginLeft: "4px",
          },
          "&:first-of-type": {
            borderRadius: "8px",
          },
        },
      },
    },
  },
  MuiSlider: {
    styleOverrides: {
      root: {
        height: "4px",
        padding: "13px 0",
        "& .MuiSlider-thumb": {
          height: "16px",
          width: "16px",
          backgroundImage:
            "linear-gradient(93deg, #BA905C 0.82%, #FFD29B 33.73%, #FFE0B1 47.65%, #FFD29B 61.53%, #BA905C 99.48%)",
          border: "1px solid currentColor",

          "&:hover": {
            boxShadow: "0 0 0 8px rgba(183, 139, 86, 0.16)",
          },
        },
        "& .MuiSlider-track": {
          height: "4px",
          backgroundImage:
            "linear-gradient(88deg, rgba(235, 191, 126, 0.00) -50.5%, #FFD29B 52.8%, rgba(235, 191, 126, 0.00) 146.18%)",
        },
        "& .MuiSlider-valueLabel": {
          fontFamily: "Kantumruy Pro",
          fontWeight: 300,
        },

        "& .MuiSlider-rail": {
          color: "#453426",
        },
      },
      valueLabel: {
        background: "rgba(99, 68, 45, 0.7)",
        color: "#FBF4EC",
      },
    },
  },
  MuiAvatar: {
    styleOverrides: {
      root: {
        border: "2px solid #ad8d75",
        zIndex: 5,
        cursor: "pointer",
        backgroundColor: "#453426",
        color: "#FFDCA8",
        transition: "border 0.3s ease-in-out, background-color 0.3s ease-in-out",
      },
    },
  },
  MuiSelect: {
    styleOverrides: {
      outlined: {
        borderRadius: "4px",
        background: "#4B3727",
        color: "#FFDCA8",
        fieldset: {
          border: 0,
        },
      },
      icon: {
        top: "calc(50% - 1rem)",
      },
    },
  },
  MuiSkeleton: {
    styleOverrides: {
      root: {
        transform: "none",
        backgroundColor: "rgba(186, 156, 126, 0.11)",
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      text: {
        color: "#FFDCA8",
        fontFamily: "Kaisei Opti",
        fontSize: "0.875rem",
        fontStyle: "normal",
        fontWeight: 400,
        lineHeight: "100%",
        letterSpacing: "0.00875rem",
        textTransform: "capitalize",
      },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        padding: "8px 16px",
        marginBottom: "8px !important",
        backgroundColor: "#FFDCA8",
        borderRadius: "8px",
        color: "#3C2D23",
        textAlign: "center",
        fontFamily: "Raleway",
        fontSize: "0.8125rem",
        fontStyle: "normal",
        fontWeight: "300",
        lineHeight: "150%",
      },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        "&:hover, &.Mui-focusVisible": { backgroundColor: "#59412E" },
      },
    },
  },
};

export default components;
